import { useMemo } from 'react'

import { matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { TrainingModuleRequestOptions } from '@models/types'

const watchEntityKeys = ['trainingModules']

export type TrainingModulesFilters = {
  string?: string,
  training_course_id?: number,
}

interface UseTrainingModulesOptions {
  entityKey?: string,
  filters?: TrainingModulesFilters,
  performHttpRequests?: boolean,
  requestOptions?: TrainingModuleRequestOptions,
}

function useTrainingModules(options: UseTrainingModulesOptions = {}) {
  const { filters = {}, requestOptions } = options
  const { string: filterString, training_course_id: filterTrainingCourseId } = filters

  const {
    updatedEntities: { trainingModules: trainingModulesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const trainingModules = useSelector(reduxState => reduxState.entities.trainingModules)

  const filteredTrainingModules = useMemo(() => {
    const filtered = Object.values(trainingModules).filter((trainingModule) => {
      const { id, title, training_course_id } = trainingModule

      const matchId = id.toString() === filterString
      const matchTitle = matchFilterString(title, filterString)
      const matchTrainingCourse = training_course_id === filterTrainingCourseId
      const matchString = matchId || matchTitle

      return matchTrainingCourse && matchString
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [trainingModulesUpdatedAt, JSON.stringify(filters)])

  const filteredTrainingModuleCount = filteredTrainingModules.length
  const hasFilteredTrainingModule = !!filteredTrainingModuleCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredTrainingModuleCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingTrainingCourses } = useReduxAction(
    'trainingModules',
    'loadTrainingModules',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      // @ts-expect-error TS2339
      shouldPerformFn: ({ loading }) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredTrainingModules,
    filteredTrainingModuleCount,
    hasFilteredTrainingModule,
    loading: loadingTrainingCourses,
  }
}

export default useTrainingModules
